import { cs } from '../../lib/currency';
import { RETIRED_TOOLS_VISIBLE } from '../../lib/retiredTools';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import api from '../../services/api';
import RefundDialog from '../../components/admin/RefundDialog';
// iter268: extracted dialogs live in ./enrollments/
import EnrollmentHistoryDialog from './enrollments/EnrollmentHistoryDialog';
import MoveEnrollmentDialog from './enrollments/MoveEnrollmentDialog';
import BulkActionDialog from './enrollments/BulkActionDialog';
import { sortEnrollments } from './enrollments/sortEnrollments';
import EnrollmentForecastMatrix from '../../components/admin/EnrollmentForecastMatrix';
import BillingCadenceCard from '../../components/admin/BillingCadenceCard';
import ImportEnrollmentsDialog from './enrollments/ImportEnrollmentsDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import ApprovalDialog from './enrollments/ApprovalDialog';
import QuickEnrollDialog from './enrollments/QuickEnrollDialog';
import MultiParentEnrollPromptDialog from './enrollments/MultiParentEnrollPromptDialog';
import RejectEnrollmentDialog from './enrollments/RejectEnrollmentDialog';
import PauseEnrollmentDialog from './enrollments/PauseEnrollmentDialog';
import CancelEnrollmentDialog from './enrollments/CancelEnrollmentDialog';
import EditPauseDialog from './enrollments/EditPauseDialog';
import EditEnrollmentDialog from './enrollments/EditEnrollmentDialog';
import EnrollmentTable from './enrollments/EnrollmentTable';
import useTablePreferences from '../../hooks/useTablePreferences';
import TablePaginationFooter from '../../components/admin/TablePaginationFooter';
import { toast } from 'sonner';
import { ClipboardList, DollarSign, Pause, PlayCircle, Play, User, Filter, Columns, ChevronDown, Search, XCircle, Download, Upload, FileText } from 'lucide-react';
import { formatDateOnly } from '../../utils/timezone';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
} from '../../components/ui/dropdown-menu';

const ENROLLMENT_TABLE_DEFAULT_COLUMNS = {
  select: false,  // Bulk selection checkbox
  enrollmentId: true,
  player: true,
  playerId: false,
  program: true,
  parent: true,
  parentId: false,
  parentEmail: false,
  parentPhone: false,
  class: true,
  classDay: false,
  classTime: false,
  status: true,
  startDate: true,
  approvedAt: true,
  pauseDate: false,
  autoResumeDate: false,
  cancelDate: false,
  fee: true,
  frequency: true,
  paymentStatus: false,
  paymentDueDate: false,
  lastPaymentDate: false,
  nextBilling: false,
  creditBalance: false,
  prorated: false,
  proratedAmount: false,
  notes: false,
  createdAt: false,
  actions: true,
};

function useTablePreferencesEnrollments() {
  const prefs = useTablePreferences('enrollments', ENROLLMENT_TABLE_DEFAULT_COLUMNS);
  return [prefs.visibleColumns, prefs.setVisibleColumns];
}

const PAYMENT_FREQUENCIES = [
  { value: 'monthly', label: 'Monthly', multiplier: 1, priceKey: 'price' },
  { value: 'quarterly', label: 'Quarterly (3 months)', multiplier: 3, priceKey: 'quarterly_price' },
  { value: 'biannual', label: 'Bi-Annual (6 months)', multiplier: 6, priceKey: 'biannual_price' },
];

const Enrollments = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [activeTab, setActiveTab] = useState('active');  // Default to Active enrollments

  // iter324 — Honor `?tab=` from the URL so the Dashboard cards can
  // deep-link into a specific tab (e.g. `?tab=pending` shows the 10
  // pending enrollments the "New Pending Enrollments" card counts).
  // Kept read-once on mount so admin tab switches don't fight the URL.
  const _routerLoc = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(_routerLoc.search || '');
    const t = (params.get('tab') || '').toLowerCase();
    if (t && ['pending', 'active', 'paused', 'canceled'].includes(t)) {
      setActiveTab(t);
    }
  }, [_routerLoc.search]);

  // iter274: pagination for the enrollments list. One shared page-size
  // and page index across tabs — the index resets to 1 whenever the tab
  // or filters change, which is what admins expect (they're now looking
  // at a different dataset). The page-size persists in localStorage so
  // an admin who prefers 100/page doesn't have to re-set it every visit.
  const [enrollPage, setEnrollPage] = useState(1);
  const [enrollPageSize, setEnrollPageSize] = useState(() => {
    try {
      const raw = parseInt(localStorage.getItem('enrollments_page_size') || '', 10);
      return [10, 25, 50, 100].includes(raw) ? raw : 25;
    } catch (_e) {
      return 25;
    }
  });
  const [approvalData, setApprovalData] = useState({
    program_id: '',
    class_id: '',
    class_ids: [], // For programs with multiple classes per week
    fee_amount: 0,
    payment_frequency: 'monthly',
    notes: '',
    start_date: new Date().toISOString().split('T')[0],
    // Pro-rate first month — defaults ON so mid-month approvals don't
    // silently bill the full monthly fee.
    apply_proration: true,
    // Discount fields
    discount_id: '',
    discount_type: '',
    discount_value: 0,
    discount_name: '',
    is_sibling_discount: false,
    final_fee: 0,
    // iter316 — Per-enrollment billing cadence override. When '' the
    // tenant default (`GET /admin/billing-cadence`) applies.
    billing_cadence: ''
  });
  
  // Sibling discount check state
  const [siblingCheck, setSiblingCheck] = useState(null);
  const [checkingSibling, setCheckingSibling] = useState(false);

  // iter271: Proration preview state (driven by the "Pro-rate first month"
  // toggle in ApprovalDialog). Fetched from
  // GET /api/enrollments/proration-preview whenever class_id / fee / start_date
  // / apply_proration change. Surfaces "$X.YZ today, then $A.BC/mo from <date>"
  // directly under the checkbox so admins see the real prorated number
  // before clicking Approve.
  const [prorationPreview, setProrationPreview] = useState(null);
  const [prorationPreviewLoading, setProrationPreviewLoading] = useState(false);
  
  // Action dialogs
  const [pauseDialog, setPauseDialog] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionData, setActionData] = useState({
    pause_date: new Date().toISOString().split('T')[0],
    auto_resume_date: '',
    cancel_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [editData, setEditData] = useState({
    monthly_fee: '',
    payment_frequency: 'monthly',
    // iter273: discount + final-price override fields for the Edit dialog.
    // Mirror the Quick Enroll / Approve shapes so the backend handler
    // accepts the same payload regardless of which flow set them.
    discount_id: '',
    discount_amount: '',
    discount_reason: '',
    final_monthly_fee: '',
    notes: '',
  });

  // History dialog
  const [historyDialog, setHistoryDialog] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Edit pause dialog (for extending/modifying auto-resume date)
  const [editPauseDialog, setEditPauseDialog] = useState(false);
  const [editPauseData, setEditPauseData] = useState({
    auto_resume_date: '',
    notes: ''
  });

  // Auto-resume data
  const [upcomingAutoResumes, setUpcomingAutoResumes] = useState([]);
  const [autoResumeLoading, setAutoResumeLoading] = useState(false);

  // Move enrollment dialog
  const [moveDialog, setMoveDialog] = useState(false);
  const [refundDialog, setRefundDialog] = useState(false);
  const [refundEnrollment, setRefundEnrollment] = useState(null);
  const [moveData, setMoveData] = useState({
    new_program_id: '',
    new_class_id: '',
    new_monthly_fee: '',
    reason: '',
    effective_date: new Date().toISOString().split('T')[0],
    notify_parent: true
  });
  const [moveLoading, setMoveLoading] = useState(false);

  // Quick Enroll dialog
  const [quickEnrollDialog, setQuickEnrollDialog] = useState(false);
  const [quickEnrollLoading, setQuickEnrollLoading] = useState(false);
  // iter321g — Multi-parent confirm prompt. When the backend refuses
  // with 409 `multi_parent_confirm_required`, we stash the detail here
  // and render the picker dialog. Selecting a candidate re-submits the
  // enrollment with `confirm_primary_parent_id` set.
  const [multiParentPrompt, setMultiParentPrompt] = useState(null);
  const [players, setPlayers] = useState([]);
  const [parents, setParents] = useState([]);
  const [quickEnrollData, setQuickEnrollData] = useState({
    player_id: '',
    program_id: '',
    class_ids: [],
    payment_frequency: 'monthly',
    monthly_fee: '',
    start_date: new Date().toISOString().split('T')[0],
    next_billing_date: '', // Empty means auto-calculate
    // Default ON — mid-month enrollments are prorated by default to
    // match the "Convert Trial → Enroll" flow. Admin can untick to bill
    // the full monthly fee on day 1.
    prorate_first_month: true,
    // iter231: discount support during enrollment creation. `discount_id`
    // pulls from the discounts catalog; `discount_amount`+`reason`
    // record a free-form admin override; `final_monthly_fee` is a hard
    // override that wins over both.
    discount_id: '',
    discount_amount: '',
    discount_reason: '',
    final_monthly_fee: '',
    // iter237: when admin explicitly enters $0 (scholarship / comp), they
    // must tick this so the backend safety guard lets it through.
    confirm_zero_price: false,
  });
  const [playerSearch, setPlayerSearch] = useState('');
  const [parentStatusFilter, setParentStatusFilter] = useState('active'); // Default to active parents
  const [playerStatusFilter, setPlayerStatusFilter] = useState('all'); // Show all players by default
  const [playerListSortDirection, setPlayerListSortDirection] = useState('asc'); // A-Z by default
  
  // Import/Export state
  const [importDialog, setImportDialog] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  // iter307be — Billing-cycles permanent export/import state
  const [billingCyclesImportDialog, setBillingCyclesImportDialog] = useState(false);
  const [billingCyclesFile, setBillingCyclesFile] = useState(null);
  const [billingCyclesDryRun, setBillingCyclesDryRun] = useState(true);
  const [billingCyclesResult, setBillingCyclesResult] = useState(null);
  const [billingCyclesLoading, setBillingCyclesLoading] = useState(false);
  const [createSubscriptions, setCreateSubscriptions] = useState(true);
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);
  
  // Sorting state for active enrollments - using single combined state to avoid stale closure issues
  const [sortState, setSortState] = useState({ field: 'approved_at', direction: 'desc' });
  const activeSortField = sortState.field;
  const activeSortDirection = sortState.direction;
  
  // Sort handler
  const handleSort = (field) => {
    setSortState(prev => {
      // iter307b-fix — Use the same DATE field set the sort util uses
      // (real backend field names). Default new sorts to DESC on date
      // columns so users see newest dates first.
      const isDateField = [
        'approved_at', 'start_date', 'pause_date', 'created_at',
        'end_date', 'next_billing_date', 'last_payment_date',
        'auto_resume_date', 'cancel_date', 'scheduled_cancel_date',
        'payment_due_date', 'updated_at', 'paid_at',
      ].includes(field);
      
      if (field === prev.field) {
        return { ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      } else {
        return { field, direction: isDateField ? 'desc' : 'asc' };
      }
    });
  };
  
  // Advanced filtering state
  const [filters, setFilters] = useState({
    player: '',
    parent: '',
    program: '',
    class: '',
    status: '',
    frequency: '',
    searchQuery: ''
  });
  
  // Column visibility state - ALL available columns
  // iter307t — Persist column selections to the user's profile so
  // they follow across devices/browsers (see useTablePreferences).
  const [visibleColumns, setVisibleColumns] = useTablePreferencesEnrollments();
  
  // Bulk selection state
  const [selectedEnrollments, setSelectedEnrollments] = useState([]);
  const [bulkActionDialog, setBulkActionDialog] = useState(false);
  const [bulkActionType, setBulkActionType] = useState('');
  const [bulkActionData, setBulkActionData] = useState({});
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Column widths for resizable columns (in pixels)
  const [_columnWidths, _setColumnWidths] = useState({
    enrollmentId: 100,
    player: 150,
    playerId: 90,
    program: 150,
    parent: 150,
    parentId: 90,
    class: 150,
    status: 100,
    startDate: 120,
    approvedAt: 100,
    pauseDate: 100,
    cancelDate: 100,
    fee: 100,
    frequency: 100,
    paymentStatus: 100,
    nextBilling: 110,
    creditBalance: 100,
    prorated: 80,
    actions: 120
  });
  
  // Filter panel visibility
  const [showFilters, setShowFilters] = useState(false);
  
  // Get unique values for filter dropdowns
  const uniqueParents = useMemo(() => {
    const parents = [...new Set(enrollments.map(e => e.parent_name).filter(Boolean))];
    return parents.sort();
  }, [enrollments]);
  
  const uniquePrograms = useMemo(() => {
    const progs = [...new Set(enrollments.map(e => e.program_name).filter(Boolean))];
    return progs.sort();
  }, [enrollments]);
  
  const uniqueClasses = useMemo(() => {
    const cls = [...new Set(enrollments.map(e => e.class_name).filter(Boolean))];
    return cls.sort();
  }, [enrollments]);
  
  const uniquePlayers = useMemo(() => {
    const plrs = [...new Set(enrollments.map(e => e.player_name).filter(Boolean))];
    return plrs.sort();
  }, [enrollments]);
  
  // Get parent IDs that match the status filter
  const activeParentIds = useMemo(() => {
    if (parentStatusFilter === 'all') {
      return parents.map(p => p.id);
    }
    return parents
      .filter(p => (p.status || 'active') === parentStatusFilter)
      .map(p => p.id);
  }, [parents, parentStatusFilter]);

  // Filter players for search and by parent status and player status
  const filteredPlayers = useMemo(() => {
    let result = players;
    
    // Filter by parent status
    if (parentStatusFilter !== 'all') {
      result = result.filter(p => 
        activeParentIds.includes(p.parent_id) || 
        activeParentIds.includes(p.staff_parent_id)
      );
    }
    
    // Filter by player status (lost_lead filter)
    if (playerStatusFilter !== 'all') {
      result = result.filter(p => (p.status || 'active') === playerStatusFilter);
    }
    
    // Filter by search query
    if (playerSearch) {
      const search = playerSearch.toLowerCase();
      result = result.filter(p => 
        (p.full_name || p.name || '').toLowerCase().includes(search) ||
        (p.parent_name || '').toLowerCase().includes(search)
      );
    }
    
    // Sort by name based on sort direction
    return result.sort((a, b) => {
      const aName = (a.full_name || a.name || '').toLowerCase();
      const bName = (b.full_name || b.name || '').toLowerCase();
      const comparison = aName.localeCompare(bName);
      return playerListSortDirection === 'asc' ? comparison : -comparison;
    });
  }, [players, activeParentIds, playerSearch, parentStatusFilter, playerStatusFilter, playerListSortDirection]);
  
  const statusOptions = ['pending', 'approved', 'current', 'active', 'paused', 'cancelled', 'rejected'];
  
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      player: '',
      parent: '',
      program: '',
      class: '',
      status: '',
      frequency: '',
      searchQuery: ''
    });
  };
  
  // Check if any filter is active
  const hasActiveFilters = Object.values(filters).some(v => v !== '');
  
  // Apply filters to enrollments
  const applyFilters = (data) => {
    return data.filter(enrollment => {
      // Search query (searches across multiple fields)
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        const searchableFields = [
          enrollment.player_name,
          enrollment.parent_name,
          enrollment.program_name,
          enrollment.class_name
        ].filter(Boolean).map(f => f.toLowerCase());
        
        if (!searchableFields.some(f => f.includes(query))) {
          return false;
        }
      }
      
      // Individual filters
      if (filters.player && enrollment.player_name !== filters.player) return false;
      if (filters.parent && enrollment.parent_name !== filters.parent) return false;
      if (filters.program && enrollment.program_name !== filters.program) return false;
      if (filters.class && enrollment.class_name !== filters.class) return false;
      if (filters.status && enrollment.status !== filters.status) return false;
      if (filters.frequency && enrollment.payment_frequency !== filters.frequency) return false;
      
      return true;
    });
  };

  const fetchData = async () => {
    try {
      const [enrollmentsRes, classesRes, programsRes, playersRes, discountsRes, parentsRes, locationsRes] = await Promise.all([
        api.get('/enrollments'),
        api.get('/classes'),
        api.get('/programs'),
        api.get('/players'),
        api.get('/discounts'),
        api.get('/parents'),
        api.get('/locations/?include_inactive=true').catch(() => ({ data: [] })),
      ]);
      setEnrollments(enrollmentsRes.data);
      setClasses(classesRes.data);
      setPrograms(programsRes.data);
      setPlayers(playersRes.data);
      setDiscounts(discountsRes.data.filter(d => d.is_active));
      setParents(parentsRes.data);
      setLocations(Array.isArray(locationsRes.data) ? locationsRes.data : []);
      
      // Fetch upcoming auto-resumes
      fetchUpcomingAutoResumes();
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingAutoResumes = async () => {
    try {
      const response = await api.get('/admin/jobs/auto-resume/upcoming');
      setUpcomingAutoResumes(response.data.upcoming || []);
    } catch (error) {
      console.error('Failed to fetch upcoming auto-resumes:', error);
    }
  };

  const handleTriggerAutoResume = async () => {
    setAutoResumeLoading(true);
    try {
      const response = await api.post('/admin/jobs/auto-resume/run');
      const result = response.data.result;
      if (result.success > 0) {
        toast.success(`Auto-resumed ${result.success} enrollment(s)`);
        fetchData(); // Refresh all data
      } else if (result.processed === 0) {
        toast.info('No enrollments due for auto-resume');
      } else {
        toast.warning(`Processed ${result.processed}, but ${result.failed} failed`);
      }
    } catch (error) {
      toast.error('Failed to run auto-resume job');
    } finally {
      setAutoResumeLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // iter316 — Race-refresh listener. When the axios 409 interceptor
  // detects a lifecycle collision (another admin approved / paused /
  // cancelled underneath us) it dispatches this window event. Re-fetch
  // the enrollment list so the row's status reflects reality.
  useEffect(() => {
    const onRaceRefresh = () => {
      try { fetchData(); } catch { /* ignore */ }
    };
    window.addEventListener('arm:race-refresh', onRaceRefresh);
    return () => window.removeEventListener('arm:race-refresh', onRaceRefresh);
  }, []);

  // iter271: Fetch proration preview whenever the approval dialog is open
  // and the inputs that drive the calculation change. We use the final
  // (post-discount) monthly fee so the preview matches what the parent
  // will actually be charged.
  useEffect(() => {
    if (!approvalDialog) {
      setProrationPreview(null);
      return;
    }
    const classId = approvalData.class_id || approvalData.class_ids?.[0];
    const startDate = approvalData.start_date;
    const baseFee = approvalData.discount_id ? approvalData.final_fee : approvalData.fee_amount;
    if (!approvalData.apply_proration || !classId || !startDate || !baseFee) {
      setProrationPreview(null);
      return;
    }
    let cancelled = false;
    setProrationPreviewLoading(true);
    api.get('/enrollments/proration-preview', {
      params: { monthly_fee: baseFee, class_id: classId, start_date: startDate },
    })
      .then((res) => { if (!cancelled) setProrationPreview(res.data); })
      .catch(() => { if (!cancelled) setProrationPreview(null); })
      .finally(() => { if (!cancelled) setProrationPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [
    approvalDialog,
    approvalData.apply_proration,
    approvalData.class_id,
    approvalData.class_ids,
    approvalData.start_date,
    approvalData.fee_amount,
    approvalData.final_fee,
    approvalData.discount_id,
  ]);

  const openApprovalDialog = async (enrollment) => {
    setSelectedEnrollment(enrollment);
    // Find the program to get pricing
    const program = programs.find(p => p.id === enrollment.program_id);
    const baseFee = program?.price || enrollment.fee_amount || 0;
    
    setApprovalData({
      program_id: enrollment.program_id || '',
      class_id: enrollment.class_id || '',
      class_ids: enrollment.class_ids || [],
      fee_amount: baseFee,
      payment_frequency: 'monthly',
      notes: '',
      start_date: new Date().toISOString().split('T')[0],
      apply_proration: true,
      // Discount fields
      discount_id: '',
      discount_type: '',
      discount_value: 0,
      discount_name: '',
      is_sibling_discount: false,
      final_fee: baseFee
    });
    setSiblingCheck(null);
    setApprovalDialog(true);
    
    // Check for sibling discount eligibility
    checkSiblingDiscount(enrollment.id);
  };

  const checkSiblingDiscount = async (enrollmentId) => {
    setCheckingSibling(true);
    try {
      const response = await api.get(`/enrollments/${enrollmentId}/sibling-discount-check`);
      setSiblingCheck(response.data);
      
      // Auto-apply sibling discount if eligible and applies to current enrollment
      if (response.data.eligible && response.data.apply_to_current_enrollment && response.data.discount) {
        const discount = response.data.discount;
        setApprovalData(prev => ({
          ...prev,
          discount_id: discount.id,
          discount_type: discount.type,
          discount_value: discount.value,
          discount_name: discount.name,
          is_sibling_discount: true,
          final_fee: discount.final_fee
        }));
      }
    } catch (error) {
      console.error('Failed to check sibling discount:', error);
      setSiblingCheck(null);
    } finally {
      setCheckingSibling(false);
    }
  };

  const handleDiscountChange = (discountId) => {
    if (!discountId || discountId === 'none') {
      setApprovalData(prev => ({
        ...prev,
        discount_id: '',
        discount_type: '',
        discount_value: 0,
        discount_name: '',
        is_sibling_discount: false,
        final_fee: prev.fee_amount
      }));
      return;
    }
    
    const discount = discounts.find(d => d.id === discountId);
    if (!discount) return;

    // iter259: discounts catalog stores the magnitude under `value`
    // (legacy frontend code used `discount_value` which is undefined →
    // discount silently became 0). Fall back to legacy so old payloads
    // still work.
    const discountValue = Number(
      discount.value !== undefined && discount.value !== null
        ? discount.value
        : (discount.discount_value || 0)
    ) || 0;

    let finalFee = approvalData.fee_amount;
    if (discount.discount_type === 'percentage') {
      finalFee = approvalData.fee_amount * (1 - discountValue / 100);
    } else {
      finalFee = Math.max(0, approvalData.fee_amount - discountValue);
    }

    setApprovalData(prev => ({
      ...prev,
      discount_id: discount.id,
      discount_type: discount.discount_type,
      discount_value: discountValue,
      discount_name: discount.name,
      is_sibling_discount: discount.name?.toLowerCase().includes('sibling') || false,
      final_fee: Math.round(finalFee * 100) / 100
    }));
  };

  const recalculateFinalFee = (baseFee, discountId = approvalData.discount_id) => {
    if (!discountId) return baseFee;

    const discount = discounts.find(d => d.id === discountId);
    if (!discount) return baseFee;

    const discountValue = Number(
      discount.value !== undefined && discount.value !== null
        ? discount.value
        : (discount.discount_value || 0)
    ) || 0;

    if (discount.discount_type === 'percentage') {
      return Math.round(baseFee * (1 - discountValue / 100) * 100) / 100;
    }
    return Math.max(0, baseFee - discountValue);
  };

  const handleProgramChange = (programId) => {
    const program = programs.find(p => p.id === programId);
    const freq = PAYMENT_FREQUENCIES.find(f => f.value === approvalData.payment_frequency);
    const price = program?.[freq?.priceKey] || program?.price || 0;
    
    setApprovalData(prev => ({
      ...prev,
      program_id: programId,
      class_id: '', // Reset class when program changes
      class_ids: [], // Reset classes when program changes
      fee_amount: price
    }));
  };

  const handleClassChange = (classId) => {
    const program = selectedProgram;
    const sessionsPerWeek = program?.sessions_per_week || 1;
    
    if (sessionsPerWeek > 1) {
      // Multi-class selection mode
      setApprovalData(prev => {
        const newClassIds = prev.class_ids.includes(classId)
          ? prev.class_ids.filter(id => id !== classId)
          : prev.class_ids.length < sessionsPerWeek 
            ? [...prev.class_ids, classId]
            : prev.class_ids; // Don't add more if at limit
        return { 
          ...prev, 
          class_ids: newClassIds,
          class_id: newClassIds[0] || '' // Keep first class as primary for backwards compatibility
        };
      });
    } else {
      // Single class selection mode
      setApprovalData(prev => ({
        ...prev,
        class_id: classId,
        class_ids: [classId]
      }));
    }
  };

  const handleFrequencyChange = (frequency) => {
    const program = programs.find(p => p.id === approvalData.program_id);
    
    // Get price based on frequency
    let price = program?.price || 0;
    if (frequency === 'quarterly' && program?.quarterly_price) {
      price = program.quarterly_price;
    } else if (frequency === 'biannual' && program?.biannual_price) {
      price = program.biannual_price;
    }
    
    const finalFee = recalculateFinalFee(price, approvalData.discount_id);
    
    setApprovalData(prev => ({
      ...prev,
      payment_frequency: frequency,
      fee_amount: price,
      final_fee: finalFee
    }));
  };

  // Get classes filtered by selected program
  const getFilteredClasses = () => {
    if (!approvalData.program_id) return [];
    return classes.filter(c => c.program_id === approvalData.program_id);
  };

  // Get selected program details
  const selectedProgram = programs.find(p => p.id === approvalData.program_id);

  const _getTotalAmount = () => {
    const freq = PAYMENT_FREQUENCIES.find(f => f.value === approvalData.payment_frequency);
    return (approvalData.fee_amount * (freq?.multiplier || 1)).toFixed(2);
  };

  const handleApprove = async () => {
    if (!approvalData.program_id) {
      toast.error('Please select a program');
      return;
    }
    
    const sessionsPerWeek = selectedProgram?.sessions_per_week || 1;
    if (sessionsPerWeek > 1) {
      if (approvalData.class_ids.length !== sessionsPerWeek) {
        toast.error(`Please select ${sessionsPerWeek} classes for this program`);
        return;
      }
    } else if (!approvalData.class_id && approvalData.class_ids.length === 0) {
      toast.error('Please select a class');
      return;
    }
    
    try {
      // Map frontend field names to backend expected names
      const payload = {
        program_id: approvalData.program_id,
        class_id: approvalData.class_id || approvalData.class_ids[0],
        class_ids: approvalData.class_ids.length > 0 ? approvalData.class_ids : [approvalData.class_id],
        payment_frequency: approvalData.payment_frequency,
        monthly_fee: approvalData.fee_amount,
        notes: approvalData.notes,
        start_date: approvalData.start_date,
        apply_proration: approvalData.apply_proration !== false,
        // Discount fields
        discount_id: approvalData.discount_id || null,
        discount_type: approvalData.discount_type || null,
        discount_value: approvalData.discount_value || null,
        discount_name: approvalData.discount_name || null,
        is_sibling_discount: approvalData.is_sibling_discount || false,
        final_monthly_fee: approvalData.discount_id ? approvalData.final_fee : null,
        // iter316 — Only send the cadence override when the admin
        // explicitly picked one; empty string means "use tenant default".
        billing_cadence: approvalData.billing_cadence || null
      };
      await api.put(`/enrollments/${selectedEnrollment.id}/approve`, payload);
      
      const discountMsg = approvalData.discount_id 
        ? ` with ${approvalData.discount_name} discount (${cs()}${(approvalData.fee_amount - approvalData.final_fee).toFixed(2)} off)`
        : '';
      toast.success(`Enrollment approved${discountMsg}`);
      setApprovalDialog(false);
      setSiblingCheck(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve enrollment');
    }
  };

  const handleReject = async (enrollmentId) => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await api.put(`/enrollments/${enrollmentId}/reject`, { reason: rejectReason });
      toast.success('Enrollment rejected and parent notified');
      setRejectDialog(false);
      setRejectReason('');
      setSelectedEnrollment(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject enrollment');
    }
  };

  const openRejectDialog = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setRejectReason('');
    setRejectDialog(true);
  };

  // Open pause dialog
  const openPauseDialog = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setActionData({
      pause_date: new Date().toISOString().split('T')[0],
      auto_resume_date: '',
      cancel_date: '',
      notes: ''
    });
    setPauseDialog(true);
  };

  // Open cancel dialog
  const openCancelDialog = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setActionData({
      pause_date: '',
      cancel_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setCancelDialog(true);
  };

  // Open edit dialog
  const openEditDialog = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setEditData({
      // Show the *original* (pre-discount) fee when the enrollment has a
      // discount applied — so editing the dialog's "Monthly Fee" knob
      // means editing the base, then the discount is layered on top.
      // Falls back to monthly_fee for older rows without an
      // original_monthly_fee snapshot.
      monthly_fee: enrollment.original_monthly_fee ?? enrollment.monthly_fee ?? '',
      payment_frequency: enrollment.payment_frequency || 'monthly',
      start_date: enrollment.start_date ? enrollment.start_date.slice(0, 10) : '',
      next_billing_date: enrollment.next_billing_date ? enrollment.next_billing_date.slice(0, 10) : '',
      last_payment_date: enrollment.last_payment_date ? enrollment.last_payment_date.slice(0, 10) : '',
      last_payment_amount: enrollment.last_payment_amount ?? '',
      payment_due_date: enrollment.payment_due_date ? enrollment.payment_due_date.slice(0, 10) : '',
      is_prorated: !!enrollment.is_prorated,
      prorated_amount: enrollment.prorated_amount ?? '',
      // Seed discount fields so the dialog opens already showing what's
      // active on the enrollment. Catalog-driven rows light up the
      // "Catalog discount" picker; free-form rows light up the custom
      // amount + reason. Final-price override is read off the actual
      // monthly_fee delta vs. original.
      discount_id: enrollment.discount_id || '',
      discount_amount: enrollment.discount_amount != null && !enrollment.discount_id
        ? enrollment.discount_amount
        : '',
      discount_reason: enrollment.discount_reason || '',
      final_monthly_fee: '',
      notes: enrollment.notes || '',
    });
    setEditDialog(true);
  };

  // Handle pause enrollment
  const handlePause = async () => {
    if (!actionData.pause_date) {
      toast.error('Please select a pause date');
      return;
    }
    // Validate auto_resume_date is after pause_date if provided
    if (actionData.auto_resume_date && actionData.auto_resume_date <= actionData.pause_date) {
      toast.error('Auto-resume date must be after pause date');
      return;
    }
    try {
      await api.put(`/admin/enrollments/${selectedEnrollment.id}/pause`, {
        pause_date: actionData.pause_date,
        auto_resume_date: actionData.auto_resume_date || null,
        // iter307ey — Send admin NBD override if set. Backend honours
        // this over the service-computed alignment.
        next_billing_date: actionData.next_billing_date || null,
        notes: actionData.notes
      });
      toast.success(actionData.auto_resume_date 
        ? `Enrollment paused. Will auto-resume on ${actionData.auto_resume_date}` 
        : 'Enrollment paused');
      setPauseDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pause enrollment');
    }
  };

  // Handle cancel enrollment
  const handleCancel = async () => {
    if (!actionData.cancel_date) {
      toast.error('Please select a cancel date');
      return;
    }
    if (!(actionData.notes || '').trim()) {
      toast.error('Notes are required to cancel an enrollment');
      return;
    }
    try {
      await api.put(`/admin/enrollments/${selectedEnrollment.id}/cancel`, {
        cancel_date: actionData.cancel_date,
        notes: actionData.notes
      });
      toast.success('Enrollment canceled');
      setCancelDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel enrollment');
    }
  };

  // Handle resume enrollment (from paused)
  const handleResume = async (enrollment) => {
    try {
      await api.put(`/admin/enrollments/${enrollment.id}/resume`);
      toast.success(`Enrollment resumed for ${enrollment.player_name}`);
      fetchData();
    } catch (error) {
      console.error('Resume error:', error);
      toast.error(error.response?.data?.detail || 'Failed to resume enrollment');
    }
  };

  // iter307ez — Cancel a pause (whether active or scheduled). Restores
  // NBD, reverses any credit granted for the pause, and puts the
  // enrollment back to its pre-pause status.
  const handleCancelPause = async (enrollment) => {
    const playerLabel = enrollment.player_name || 'enrollment';
    const isScheduled = !!enrollment.scheduled_pause_date && enrollment.status !== 'paused';
    const summary = isScheduled
      ? `The scheduled pause (from ${enrollment.scheduled_pause_date}) will be canceled.`
      : `The active pause will be canceled and the enrollment resumed.`;
    if (!window.confirm(
      `Cancel pause for ${playerLabel}?\n\n${summary}\n\n` +
      `• Next billing date will be restored to before the pause.\n` +
      `• Any credit granted for this pause will be reversed.\n` +
      `• Status returns to what it was before pause.`
    )) {
      return;
    }
    try {
      const { data } = await api.post(
        `/admin/enrollments/${enrollment.id}/cancel-pause`
      );
      const bits = [];
      if (data.credit_reversed > 0) bits.push(`Credit reversed: ${cs()}${data.credit_reversed.toFixed(2)}`);
      if (data.restored_nbd) bits.push(`NBD: ${data.restored_nbd}`);
      toast.success(
        `Pause canceled for ${playerLabel}` + (bits.length ? ` (${bits.join(', ')})` : '')
      );
      fetchData();
    } catch (error) {
      console.error('Cancel-pause error:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel pause');
    }
  };

  // Recalculate next_billing_date from invoice history
  const handleRecalculateBilling = async (enrollment) => {
    const playerLabel = enrollment.player_name || 'enrollment';
    const previous = enrollment.next_billing_date
      ? new Date(enrollment.next_billing_date).toLocaleDateString()
      : 'unset';
    if (!window.confirm(
      `Recalculate the next bill date for ${playerLabel}?\n\n` +
      `Current: ${previous}\n\n` +
      `This reads the enrollment's invoice history and sets the cursor to the correct next period. Use this if a payment double-advanced the date and skipped a month.`
    )) {
      return;
    }
    try {
      const { data } = await api.post(
        `/admin/enrollments/${enrollment.id}/recalculate-next-billing-date`
      );
      if (data.previous === data.next_billing_date) {
        toast.info(`No change — already at ${new Date(data.next_billing_date).toLocaleDateString()}`);
      } else {
        toast.success(
          `Updated: ${data.previous ? new Date(data.previous).toLocaleDateString() : '—'} → ${new Date(data.next_billing_date).toLocaleDateString()}`
        );
      }
      fetchData();
    } catch (error) {
      console.error('Recalculate billing error:', error);
      toast.error(error.response?.data?.detail || 'Failed to recalculate bill date');
    }
  };

  // Open edit pause dialog (for modifying auto-resume date on paused enrollment)
  const openEditPauseDialog = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setEditPauseData({
      auto_resume_date: enrollment.auto_resume_date || '',
      notes: ''
    });
    setEditPauseDialog(true);
  };

  // Handle edit pause (update auto-resume date)
  const handleEditPause = async () => {
    try {
      await api.put(`/admin/enrollments/${selectedEnrollment.id}/update-pause`, {
        auto_resume_date: editPauseData.auto_resume_date || null,
        notes: editPauseData.notes
      });
      toast.success(editPauseData.auto_resume_date 
        ? `Auto-resume date updated to ${editPauseData.auto_resume_date}` 
        : 'Auto-resume date cleared');
      setEditPauseDialog(false);
      fetchData();
    } catch (error) {
      console.error('Edit pause error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update pause');
    }
  };

  // Handle edit enrollment
  const handleEdit = async () => {
    try {
      const payload = {
        monthly_fee: editData.monthly_fee !== '' ? parseFloat(editData.monthly_fee) : null,
        payment_frequency: editData.payment_frequency,
        start_date: editData.start_date || null,
        next_billing_date: editData.next_billing_date || null,
        last_payment_date: editData.last_payment_date || null,
        last_payment_amount: editData.last_payment_amount !== '' ? parseFloat(editData.last_payment_amount) : null,
        payment_due_date: editData.payment_due_date || null,
        is_prorated: !!editData.is_prorated,
        prorated_amount: editData.prorated_amount !== '' ? parseFloat(editData.prorated_amount) : null,
        notes: editData.notes,
        // iter273: discount + final-price override fields. Backend re-runs
        // the calculation from the base `monthly_fee` (above) − discount,
        // then `final_monthly_fee` (if set) takes precedence. Sending an
        // empty discount_id clears any previously applied catalog discount.
        discount_id: editData.discount_id || '',
        discount_amount: editData.discount_amount !== '' && !editData.discount_id
          ? parseFloat(editData.discount_amount)
          : null,
        discount_reason: editData.discount_reason || null,
        final_monthly_fee: editData.final_monthly_fee !== ''
          ? parseFloat(editData.final_monthly_fee)
          : null,
      };
      await api.put(`/admin/enrollments/${selectedEnrollment.id}`, payload);
      toast.success('Enrollment updated');
      setEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update enrollment');
    }
  };

  // Open move dialog
  const openMoveDialog = (enrollment) => {
    setSelectedEnrollment(enrollment);
    const program = programs.find(p => p.id === enrollment.program_id);
    setMoveData({
      new_program_id: enrollment.program_id || '',
      new_class_id: '',
      new_monthly_fee: enrollment.monthly_fee || program?.price || '',
      reason: '',
      effective_date: new Date().toISOString().split('T')[0],
      notify_parent: true
    });
    setMoveDialog(true);
  };

  // Handle program change in move dialog
  const handleMoveProgramChange = (programId) => {
    const program = programs.find(p => p.id === programId);
    setMoveData(prev => ({
      ...prev,
      new_program_id: programId,
      new_class_id: '', // Reset class when program changes
      new_monthly_fee: program?.price || ''
    }));
  };

  // Get classes filtered by selected program for move dialog
  const getMoveFilteredClasses = () => {
    if (!moveData.new_program_id) return [];
    return classes.filter(c => c.program_id === moveData.new_program_id);
  };

  // Handle move enrollment
  const handleMove = async () => {
    if (!moveData.new_program_id) {
      toast.error('Please select a program');
      return;
    }
    if (!moveData.new_class_id) {
      toast.error('Please select a class');
      return;
    }
    
    setMoveLoading(true);
    try {
      const payload = {
        new_program_id: moveData.new_program_id,
        new_class_id: moveData.new_class_id,
        new_monthly_fee: moveData.new_monthly_fee ? parseFloat(moveData.new_monthly_fee) : null,
        reason: moveData.reason,
        effective_date: moveData.effective_date,
        notify_parent: moveData.notify_parent
      };
      
      const response = await api.put(`/admin/enrollments/${selectedEnrollment.id}/move`, payload);
      toast.success(`Player moved to ${response.data.to_program} - ${response.data.to_class}`);
      setMoveDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move enrollment');
    } finally {
      setMoveLoading(false);
    }
  };

  // Open history dialog
  const openHistoryDialog = async (enrollment) => {
    setSelectedEnrollment(enrollment);
    setHistoryLoading(true);
    setHistoryDialog(true);
    try {
      const response = await api.get(`/admin/enrollments/${enrollment.id}/history`);
      setHistoryData(response.data);
    } catch (error) {
      toast.error('Failed to load enrollment history');
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Format action name for display
  // iter268: `formatActionName` + `getActionColor` moved into
  // EnrollmentHistoryDialog (their only consumer).

  // ============================================================
  // BULK SELECTION & ACTIONS
  // ============================================================
  
  // Toggle selection for a single enrollment
  const toggleEnrollmentSelection = (enrollmentId) => {
    setSelectedEnrollments(prev => {
      if (prev.includes(enrollmentId)) {
        return prev.filter(id => id !== enrollmentId);
      } else {
        return [...prev, enrollmentId];
      }
    });
  };
  
  // Select/deselect all visible enrollments in current tab
  const toggleSelectAll = (enrollmentsList) => {
    const visibleIds = enrollmentsList.map(e => e.id);
    const allSelected = visibleIds.every(id => selectedEnrollments.includes(id));
    
    if (allSelected) {
      // Deselect all visible
      setSelectedEnrollments(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      // Select all visible
      setSelectedEnrollments(prev => [...new Set([...prev, ...visibleIds])]);
    }
  };
  
  // Clear all selections
  const clearSelection = () => {
    setSelectedEnrollments([]);
  };
  
  // Open bulk action dialog
  const openBulkActionDialog = (actionType) => {
    setBulkActionType(actionType);
    setBulkActionData({
      status: '',
      monthly_fee: '',
      payment_frequency: '',
      pause_date: new Date().toISOString().split('T')[0],
      auto_resume_date: '',
      cancel_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setBulkActionDialog(true);
  };
  
  // Execute bulk action
  const executeBulkAction = async () => {
    if (selectedEnrollments.length === 0) {
      toast.error('No enrollments selected');
      return;
    }
    
    setBulkActionLoading(true);
    let successCount = 0;
    let failCount = 0;
    
    try {
      for (const enrollmentId of selectedEnrollments) {
        try {
          switch (bulkActionType) {
            case 'pause':
              await api.put(`/admin/enrollments/${enrollmentId}/pause`, {
                pause_date: bulkActionData.pause_date,
                auto_resume_date: bulkActionData.auto_resume_date || null,
                notes: bulkActionData.notes
              });
              break;
            case 'resume':
              await api.put(`/admin/enrollments/${enrollmentId}/resume`);
              break;
            case 'cancel':
              await api.put(`/admin/enrollments/${enrollmentId}/cancel`, {
                cancel_date: bulkActionData.cancel_date,
                notes: bulkActionData.notes
              });
              break;
            case 'updateFee':
              await api.put(`/admin/enrollments/${enrollmentId}`, {
                monthly_fee: bulkActionData.monthly_fee ? parseFloat(bulkActionData.monthly_fee) : undefined,
                payment_frequency: bulkActionData.payment_frequency || undefined,
                notes: bulkActionData.notes
              });
              break;
            default:
              throw new Error('Unknown action type');
          }
          successCount++;
        } catch (err) {
          failCount++;
          console.error(`Failed to ${bulkActionType} enrollment ${enrollmentId}:`, err);
        }
      }
      
      if (successCount > 0) {
        toast.success(`Successfully processed ${successCount} enrollment(s)`);
      }
      if (failCount > 0) {
        toast.error(`Failed to process ${failCount} enrollment(s)`);
      }
      
      setBulkActionDialog(false);
      clearSelection();
      fetchData();
    } catch (error) {
      toast.error('Bulk action failed');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const pendingEnrollments = sortEnrollments(
    applyFilters(enrollments.filter(e => e.status === 'pending')),
    activeSortField, activeSortDirection,
  );
  const activeEnrollments = sortEnrollments(
    applyFilters(enrollments.filter(e => ['approved', 'current', 'active'].includes(e.status))),
    activeSortField, activeSortDirection,
  );
  const pausedEnrollments = sortEnrollments(
    applyFilters(enrollments.filter(e => e.status === 'paused')),
    activeSortField, activeSortDirection,
  );
  const canceledEnrollments = sortEnrollments(
    applyFilters(enrollments.filter(e => ['canceled', 'cancelled', 'rejected'].includes(e.status))),
    activeSortField, activeSortDirection,
  );

  // iter274: reset pagination to page 1 whenever the active tab or the
  // applied filters change. Otherwise an admin sitting on page 5 of
  // Active and then switching to Pending would land on an empty page 5.
  useEffect(() => { setEnrollPage(1); }, [activeTab, filters]);

  // Persist the page-size preference so the choice survives reloads.
  useEffect(() => {
    try { localStorage.setItem('enrollments_page_size', String(enrollPageSize)); } catch (_e) { /* ignore */ }
  }, [enrollPageSize]);

  // Defensive clamp: if filters shrink the dataset while we're on a
  // later page, snap back to the last valid page on the next render.
  // Computed off the currently visible tab's row count.
  const currentTabCount = (
    activeTab === 'pending' ? pendingEnrollments.length
      : activeTab === 'active' ? activeEnrollments.length
        : activeTab === 'paused' ? pausedEnrollments.length
          : canceledEnrollments.length
  );
  const enrollTotalPages = Math.max(1, Math.ceil(currentTabCount / enrollPageSize));
  if (enrollPage > enrollTotalPages && enrollPage !== 1) {
    setTimeout(() => setEnrollPage(enrollTotalPages), 0);
  }
  const sliceForTab = (rows) => rows.slice(
    (enrollPage - 1) * enrollPageSize,
    enrollPage * enrollPageSize,
  );

  const renderEnrollmentsPagination = (totalForTab) => (
    <TablePaginationFooter
      currentPage={enrollPage}
      totalPages={Math.max(1, Math.ceil(totalForTab / enrollPageSize))}
      itemsPerPage={enrollPageSize}
      totalItems={totalForTab}
      itemLabel="enrollment"
      onPrevPage={() => setEnrollPage((p) => Math.max(1, p - 1))}
      onNextPage={() => setEnrollPage((p) => p + 1)}
      onItemsPerPageChange={(n) => { setEnrollPageSize(n); setEnrollPage(1); }}
      testIdPrefix="enrollments-pagination"
    />
  );
  
  if (loading) {
    return (
      <DashboardLayout title="Enrollments">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  // Quick Enroll handlers
  const openQuickEnrollDialog = () => {
    setQuickEnrollData({
      player_id: '',
      program_id: '',
      class_ids: [],
      payment_frequency: 'monthly',
      monthly_fee: '',
      start_date: new Date().toISOString().split('T')[0],
      next_billing_date: '', // Empty means auto-calculate
      prorate_first_month: true,
      // iter294: reset ALL discount/override fields (iter231 + iter237).
      // Previously these were left `undefined` after a dialog re-open,
      // which made the submit guard below send `final_monthly_fee: 0`
      // (parseFloat(undefined)→NaN → `|| 0` → 0). That silently
      // overrode the program's base price to $0 and tripped the
      // backend zero-fee guard with the misleading "$0/month" error.
      final_monthly_fee: '',
      discount_id: '',
      discount_amount: '',
      discount_reason: '',
      confirm_zero_price: false,
    });
    setPlayerSearch('');
    setQuickEnrollDialog(true);
  };

  const handleQuickEnrollProgramChange = (programId) => {
    const program = programs.find(p => p.id === programId);
    const freq = PAYMENT_FREQUENCIES.find(f => f.value === quickEnrollData.payment_frequency);
    const price = program?.[freq?.priceKey] || program?.price || 0;
    
    setQuickEnrollData(prev => ({
      ...prev,
      program_id: programId,
      class_ids: [],
      monthly_fee: price
    }));
  };

  const handleQuickEnrollClassToggle = (classId) => {
    setQuickEnrollData(prev => {
      const newClassIds = prev.class_ids.includes(classId)
        ? prev.class_ids.filter(id => id !== classId)
        : [...prev.class_ids, classId];
      return { ...prev, class_ids: newClassIds };
    });
  };

  const handleQuickEnrollFrequencyChange = (frequency) => {
    const program = programs.find(p => p.id === quickEnrollData.program_id);
    let price = program?.price || 0;
    if (frequency === 'quarterly' && program?.quarterly_price) {
      price = program.quarterly_price;
    } else if (frequency === 'biannual' && program?.biannual_price) {
      price = program.biannual_price;
    }
    
    setQuickEnrollData(prev => ({
      ...prev,
      payment_frequency: frequency,
      monthly_fee: price
    }));
  };

  const handleQuickEnroll = async (
    confirmZeroPriceOverride = false,
    confirmPrimaryParentIdOverride = null,
  ) => {
    if (!quickEnrollData.player_id) {
      toast.error('Please select a player');
      return;
    }
    if (quickEnrollData.class_ids.length === 0) {
      toast.error('Please select at least one class');
      return;
    }

    setQuickEnrollLoading(true);
    try {
      const payload = {
        player_id: quickEnrollData.player_id,
        program_id: quickEnrollData.program_id,
        class_ids: quickEnrollData.class_ids,
        payment_frequency: quickEnrollData.payment_frequency,
        monthly_fee: parseFloat(quickEnrollData.monthly_fee) || null,
        status: 'current',
        start_date: quickEnrollData.start_date,
        // Pass the admin's pro-rate intent through. Backend defaults to
        // true if absent, but we send it explicitly so unticking the
        // checkbox actually disables proration.
        prorate_first_month: !!quickEnrollData.prorate_first_month,
      };
      // iter231: send discount inputs only when populated so backend
      // can distinguish between "no discount" and "empty string".
      // iter294: also exclude `undefined` (stale-state safety net).
      const isUnset = (v) => v === undefined || v === null || v === '';
      if (quickEnrollData.discount_id) payload.discount_id = quickEnrollData.discount_id;
      if (!isUnset(quickEnrollData.discount_amount)) {
        payload.discount_amount = parseFloat(quickEnrollData.discount_amount) || 0;
      }
      if (quickEnrollData.discount_reason) payload.discount_reason = quickEnrollData.discount_reason;
      if (!isUnset(quickEnrollData.final_monthly_fee)) {
        payload.final_monthly_fee = parseFloat(quickEnrollData.final_monthly_fee) || 0;
      }
      // iter237: confirm explicit $0 enrollments so the backend guard
      // doesn't block intentional scholarships / comps.
      if (quickEnrollData.confirm_zero_price || confirmZeroPriceOverride) {
        payload.confirm_zero_price = true;
      }
      
      // Only include next_billing_date if admin specified it (for data imports)
      if (quickEnrollData.next_billing_date) {
        payload.next_billing_date = quickEnrollData.next_billing_date;
      }

      // iter321g — Confirmed primary parent (only present after the
      // admin picks one from the multi-parent prompt on retry).
      if (confirmPrimaryParentIdOverride) {
        payload.confirm_primary_parent_id = confirmPrimaryParentIdOverride;
      }

      const response = await api.post('/admin/enrollments', payload);

      // Show detailed success message
      if (response.data.is_prorated) {
        toast.success(
          `Enrolled ${response.data.player_name}! Prorated: ${cs()}${response.data.prorated_amount} (${response.data.proration_sessions}/${response.data.total_sessions} sessions). Next billing: ${response.data.next_billing_date}`,
          { duration: 6000 }
        );
      } else {
        toast.success(
          `Enrolled ${response.data.player_name} in ${response.data.program_name}. Monthly: ${cs()}${response.data.monthly_fee}`,
          { duration: 4000 }
        );
      }

      setQuickEnrollDialog(false);
      setMultiParentPrompt(null);
      fetchData();
    } catch (error) {
      // iter321g — Multi-parent picker required. Backend returns 409
      // with `{code: "multi_parent_confirm_required", candidates: [...]}`.
      // Stash the detail + open the picker; on choice we re-call with
      // the override.
      const status = error.response?.status;
      const detailObj = error.response?.data?.detail;
      if (status === 409 && detailObj && detailObj.code === 'multi_parent_confirm_required') {
        setMultiParentPrompt(detailObj);
        return;
      }
      // iter294: backend $0-guard fallback. If the backend rejects the
      // create because monthly_fee resolved to $0 (legit scholarship,
      // OR a stale-state edge case), prompt the admin to confirm and
      // retry with `confirm_zero_price=true`. Detection uses the literal
      // marker in the error message (set in routers/admin.py iter237).
      const detail = typeof detailObj === 'string' ? detailObj : (detailObj?.message || '');
      if (
        error.response?.status === 400
        && /confirm_zero_price=true/.test(detail)
        && !confirmZeroPriceOverride
        && !quickEnrollData.confirm_zero_price
      ) {
        const ok = window.confirm(
          `${detail}\n\nClick OK to create as a scholarship (final $0/month). `
          + 'Cancel to go back and review the amounts.'
        );
        if (ok) {
          setQuickEnrollLoading(false);
          // Retry with the explicit override so we don't depend on a
          // not-yet-flushed state update.
          handleQuickEnroll(true);
          return;
        }
      }
      toast.error(detail || 'Failed to enroll player');
    } finally {
      setQuickEnrollLoading(false);
    }
  };

  // ===== IMPORT/EXPORT FUNCTIONS =====
  
  const handleExport = async () => {
    try {
      const response = await api.get('/admin/enrollments/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `enrollments_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Enrollments exported successfully');
    } catch (error) {
      toast.error('Failed to export enrollments');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/admin/enrollments/export-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'enrollment_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Please select a file to import');
      return;
    }

    setImportLoading(true);
    const formData = new FormData();
    formData.append('file', importFile);

    try {
      const response = await api.post(
        `/admin/enrollments/bulk-upload?create_subscriptions=${createSubscriptions}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setImportResults(response.data);
      
      if (response.data.created > 0 || response.data.updated > 0) {
        toast.success(
          `Import complete: ${response.data.created} created, ${response.data.updated} updated, ${response.data.subscriptions_created} subscriptions created`
        );
        fetchData();
      }
      
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} row(s) had errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import enrollments');
    } finally {
      setImportLoading(false);
    }
  };

  const resetImportDialog = () => {
    setImportDialog(false);
    setImportFile(null);
    setImportResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // iter307be — Billing-cycles handlers (permanent CSV export + import)
  const handleExportBillingCycles = async () => {
    try {
      const response = await api.get(
        '/enrollments/admin/billing-cycles/export',
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `billing-cycles-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Billing cycles exported');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    }
  };

  const handleImportBillingCycles = async () => {
    if (!billingCyclesFile) {
      toast.error('Please choose a CSV file first');
      return;
    }
    setBillingCyclesLoading(true);
    setBillingCyclesResult(null);
    try {
      const formData = new FormData();
      formData.append('file', billingCyclesFile);
      const { data } = await api.post(
        `/enrollments/admin/billing-cycles/import?dry_run=${billingCyclesDryRun}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setBillingCyclesResult(data);
      const s = data.summary || {};
      if (billingCyclesDryRun) {
        toast.success(
          `Dry-run preview: ${s.deleted || 0} delete, ${s.updated_invoices || 0} MI update, ${s.updated_enrollments || 0} enrollment update`,
        );
      } else {
        toast.success(
          `Committed: ${s.deleted || 0} deleted, ${s.updated_invoices || 0} MI updated, ${s.updated_enrollments || 0} enrollments updated`,
        );
        fetchData();
      }
      if (s.errors > 0) {
        toast.warning(`${s.errors} row error(s) — see list in dialog`, { duration: 9000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setBillingCyclesLoading(false);
    }
  };

  const resetBillingCyclesDialog = () => {
    setBillingCyclesImportDialog(false);
    setBillingCyclesFile(null);
    setBillingCyclesResult(null);
    setBillingCyclesDryRun(true);
  };


  // Get classes for quick enroll
  const quickEnrollClasses = quickEnrollData.program_id 
    ? classes.filter(c => c.program_id === quickEnrollData.program_id && c.is_active !== false)
    : [];

  return (
    <DashboardLayout title="Enrollments">
      <div className="space-y-6" data-testid="enrollments-page">
        <div className="space-y-3">
          <p className="text-slate-600">Manage enrollment requests and assignments</p>
          <div className="flex flex-wrap items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="import-export-btn">
                  <FileText className="h-4 w-4 mr-2" />
                  Import/Export
                  <ChevronDown className="h-4 w-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Data Management</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleExport}>
                  <Download className="h-4 w-4 mr-2" />
                  Export All Enrollments
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDownloadTemplate}>
                  <FileText className="h-4 w-4 mr-2" />
                  Download Import Template
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setImportDialog(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  Import Enrollments
                </DropdownMenuItem>
                {/* iter307be — permanent CSV buttons for the billing-cycles
                    export/import endpoints (the one-time tooling shipped in
                    iter307ax is now first-class). */}
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Billing Cycles</DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={handleExportBillingCycles}
                  data-testid="export-billing-cycles-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Billing Cycles (CSV)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setBillingCyclesImportDialog(true)}
                  data-testid="import-billing-cycles-btn"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Import Billing Cycles (Update / Delete)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button 
              onClick={openQuickEnrollDialog} 
              className="bg-green-600 hover:bg-green-700"
              data-testid="quick-enroll-btn"
            >
              <User className="h-4 w-4 mr-2" />
              Quick Enroll
            </Button>
          </div>
        </div>

        {/* Upcoming Auto-Resumes Alert */}
        {upcomingAutoResumes.length > 0 && (
          <Card className="border border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-full">
                    <PlayCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-green-900">
                      {upcomingAutoResumes.filter(r => r.is_due).length > 0 
                        ? `${upcomingAutoResumes.filter(r => r.is_due).length} enrollment(s) due for auto-resume today`
                        : `${upcomingAutoResumes.length} upcoming auto-resume(s)`
                      }
                    </h3>
                    <p className="text-sm text-green-700">
                      {upcomingAutoResumes.map(r => `${r.player_name} (${r.auto_resume_date})`).join(', ')}
                    </p>
                  </div>
                </div>
                {upcomingAutoResumes.filter(r => r.is_due).length > 0 && (
                  <Button 
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={handleTriggerAutoResume}
                    disabled={autoResumeLoading}
                    data-testid="trigger-auto-resume-btn"
                  >
                    {autoResumeLoading ? (
                      <span className="animate-spin mr-1">⏳</span>
                    ) : (
                      <PlayCircle className="h-4 w-4 mr-1" />
                    )}
                    Run Auto-Resume Now
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filter & Column Controls */}
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Search Input */}
              <div className="flex-1 min-w-[200px] max-w-sm">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search player, parent, program..."
                    value={filters.searchQuery}
                    onChange={(e) => setFilters(f => ({ ...f, searchQuery: e.target.value }))}
                    className="pl-9"
                    data-testid="search-enrollments"
                  />
                  {filters.searchQuery && (
                    <button 
                      onClick={() => setFilters(f => ({ ...f, searchQuery: '' }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <XCircle className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              
              {/* Filter Toggle Button */}
              <Button 
                variant={showFilters ? "default" : "outline"}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters-btn"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <Badge className="ml-2 bg-blue-500 text-white text-xs px-1.5">
                    {Object.values(filters).filter(v => v !== '').length}
                  </Badge>
                )}
              </Button>
              
              {/* Column Visibility Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="columns-btn">
                    <Columns className="h-4 w-4 mr-2" />
                    Columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 max-h-80 overflow-y-auto">
                  <DropdownMenuLabel>Toggle Columns</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.enrollmentId}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, enrollmentId: checked }))}
                  >
                    Enrollment ID
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.player}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, player: checked }))}
                  >
                    Player
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.playerId}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, playerId: checked }))}
                  >
                    Player ID
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.program}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, program: checked }))}
                  >
                    Program
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.parent}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, parent: checked }))}
                  >
                    Parent
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.parentId}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, parentId: checked }))}
                  >
                    Parent ID
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.class}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, class: checked }))}
                  >
                    Class
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.status}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, status: checked }))}
                  >
                    Status
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.startDate}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, startDate: checked }))}
                  >
                    Class Start
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.approvedAt}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, approvedAt: checked }))}
                  >
                    Approved Date
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.pauseDate}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, pauseDate: checked }))}
                  >
                    Pause Date
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.cancelDate}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, cancelDate: checked }))}
                  >
                    Cancel Date
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-slate-500">Payment & Billing</DropdownMenuLabel>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.fee}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, fee: checked }))}
                  >
                    Fee
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.frequency}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, frequency: checked }))}
                  >
                    Frequency
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.paymentStatus}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, paymentStatus: checked }))}
                  >
                    Payment Status
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.paymentDueDate}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, paymentDueDate: checked }))}
                  >
                    Payment Due Date
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.lastPaymentDate}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, lastPaymentDate: checked }))}
                  >
                    Last Payment
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.nextBilling}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, nextBilling: checked }))}
                  >
                    Next Billing
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.creditBalance}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, creditBalance: checked }))}
                  >
                    Credit Balance
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.prorated}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, prorated: checked }))}
                  >
                    Prorated
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.proratedAmount}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, proratedAmount: checked }))}
                  >
                    Prorated Amount
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-slate-500">Additional</DropdownMenuLabel>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.parentEmail}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, parentEmail: checked }))}
                  >
                    Parent Email
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.parentPhone}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, parentPhone: checked }))}
                  >
                    Parent Phone
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.classDay}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, classDay: checked }))}
                  >
                    Class Day
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.classTime}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, classTime: checked }))}
                  >
                    Class Time
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.autoResumeDate}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, autoResumeDate: checked }))}
                  >
                    Auto Resume Date
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.notes}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, notes: checked }))}
                  >
                    Notes
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.createdAt}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, createdAt: checked }))}
                  >
                    Created Date
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.select}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, select: checked }))}
                  >
                    Selection Checkbox
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.actions}
                    onCheckedChange={(checked) => setVisibleColumns(v => ({ ...v, actions: checked }))}
                  >
                    Actions
                  </DropdownMenuCheckboxItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={clearFilters}
                  className="text-slate-500"
                  data-testid="clear-filters-btn"
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
            
            {/* Bulk Actions Toolbar - Shows when items are selected */}
            {selectedEnrollments.length > 0 && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-blue-700">
                    {selectedEnrollments.length} enrollment(s) selected
                  </span>
                  <Button variant="ghost" size="sm" onClick={clearSelection} className="text-blue-600">
                    <XCircle className="h-4 w-4 mr-1" />
                    Clear Selection
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="default" size="sm" data-testid="bulk-actions-btn">
                        Bulk Actions
                        <ChevronDown className="h-4 w-4 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Update Selected</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => openBulkActionDialog('pause')}>
                        <Pause className="h-4 w-4 mr-2" />
                        Pause Enrollments
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openBulkActionDialog('resume')}>
                        <Play className="h-4 w-4 mr-2" />
                        Resume Enrollments
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openBulkActionDialog('cancel')}>
                        <XCircle className="h-4 w-4 mr-2" />
                        Cancel Enrollments
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => openBulkActionDialog('updateFee')}>
                        <DollarSign className="h-4 w-4 mr-2" />
                        Update Fee / Frequency
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            )}
            
            {/* Expanded Filter Panel */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {/* Player Filter */}
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Player</Label>
                    <Select 
                      value={filters.player} 
                      onValueChange={(v) => setFilters(f => ({ ...f, player: v === 'all' ? '' : v }))}
                    >
                      <SelectTrigger data-testid="filter-player">
                        <SelectValue placeholder="All Players" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Players</SelectItem>
                        {uniquePlayers.map(player => (
                          <SelectItem key={player} value={player}>{player}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Parent Filter */}
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Parent</Label>
                    <Select 
                      value={filters.parent} 
                      onValueChange={(v) => setFilters(f => ({ ...f, parent: v === 'all' ? '' : v }))}
                    >
                      <SelectTrigger data-testid="filter-parent">
                        <SelectValue placeholder="All Parents" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Parents</SelectItem>
                        {uniqueParents.map(parent => (
                          <SelectItem key={parent} value={parent}>{parent}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Program Filter */}
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Program</Label>
                    <Select 
                      value={filters.program} 
                      onValueChange={(v) => setFilters(f => ({ ...f, program: v === 'all' ? '' : v }))}
                    >
                      <SelectTrigger data-testid="filter-program">
                        <SelectValue placeholder="All Programs" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Programs</SelectItem>
                        {uniquePrograms.map(prog => (
                          <SelectItem key={prog} value={prog}>{prog}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Class Filter */}
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Class</Label>
                    <Select 
                      value={filters.class} 
                      onValueChange={(v) => setFilters(f => ({ ...f, class: v === 'all' ? '' : v }))}
                    >
                      <SelectTrigger data-testid="filter-class">
                        <SelectValue placeholder="All Classes" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Classes</SelectItem>
                        {uniqueClasses.map(cls => (
                          <SelectItem key={cls} value={cls}>{cls}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Status Filter */}
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Status</Label>
                    <Select 
                      value={filters.status} 
                      onValueChange={(v) => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}
                    >
                      <SelectTrigger data-testid="filter-status">
                        <SelectValue placeholder="All Statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        {statusOptions.map(status => (
                          <SelectItem key={status} value={status} className="capitalize">{status}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Frequency Filter */}
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Frequency</Label>
                    <Select 
                      value={filters.frequency} 
                      onValueChange={(v) => setFilters(f => ({ ...f, frequency: v === 'all' ? '' : v }))}
                    >
                      <SelectTrigger data-testid="filter-frequency">
                        <SelectValue placeholder="All Frequencies" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Frequencies</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="biannual">Bi-Annual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="inline-flex w-auto">
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pending ({pendingEnrollments.length})
            </TabsTrigger>
            <TabsTrigger value="active" data-testid="tab-active">
              Active ({activeEnrollments.length})
            </TabsTrigger>
            <TabsTrigger value="paused" data-testid="tab-paused">
              Paused ({pausedEnrollments.length})
            </TabsTrigger>
            <TabsTrigger value="canceled" data-testid="tab-canceled">
              Canceled ({canceledEnrollments.length})
            </TabsTrigger>
            <TabsTrigger value="forecast" data-testid="tab-forecast">
              Forecast
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-6">
            <Card className="border border-slate-200">
              <CardContent className="p-0">
                {pendingEnrollments.length === 0 ? (
                  <div className="p-12 text-center">
                    <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">{hasActiveFilters ? 'No pending enrollments match your filters' : 'No pending enrollment requests'}</p>
                  </div>
                ) : (
                  <>
                    <EnrollmentTable 
                      data={sliceForTab(pendingEnrollments)} 
                      showActions 
                      actionType="pending"
                      sortField={activeSortField}
                      sortDirection={activeSortDirection}
                      onSort={handleSort}
                      visibleColumns={visibleColumns}
                      selectedEnrollments={selectedEnrollments}
                      onToggleSelection={toggleEnrollmentSelection}
                      onSelectAll={toggleSelectAll}
                      onApprove={openApprovalDialog}
                      onReject={openRejectDialog}
                      onEdit={openEditDialog}
                      onMove={openMoveDialog}
                      onPause={openPauseDialog}
                      onCancel={openCancelDialog}
                      onResume={handleResume}
                      onCancelPause={handleCancelPause}
                      onEditPause={openEditPauseDialog}
                      onHistory={openHistoryDialog}
                      onRefund={(enr) => { setRefundEnrollment(enr); setRefundDialog(true); }}
                      onRecalculateBilling={RETIRED_TOOLS_VISIBLE ? handleRecalculateBilling : undefined}
                    />
                    {renderEnrollmentsPagination(pendingEnrollments.length)}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="active" className="mt-6">
            <Card className="border border-slate-200">
              <CardContent className="p-0">
                {activeEnrollments.length === 0 ? (
                  <div className="p-12 text-center">
                    <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">{hasActiveFilters ? 'No active enrollments match your filters' : 'No active enrollments'}</p>
                  </div>
                ) : (
                  <>
                    <EnrollmentTable 
                      data={sliceForTab(activeEnrollments)} 
                      showDates 
                      actionType="active"
                      sortField={activeSortField}
                      sortDirection={activeSortDirection}
                      onSort={handleSort}
                      visibleColumns={visibleColumns}
                      selectedEnrollments={selectedEnrollments}
                      onToggleSelection={toggleEnrollmentSelection}
                      onSelectAll={toggleSelectAll}
                      onApprove={openApprovalDialog}
                      onReject={openRejectDialog}
                      onEdit={openEditDialog}
                      onMove={openMoveDialog}
                      onPause={openPauseDialog}
                      onCancel={openCancelDialog}
                      onResume={handleResume}
                      onCancelPause={handleCancelPause}
                      onEditPause={openEditPauseDialog}
                      onHistory={openHistoryDialog}
                      onRefund={(enr) => { setRefundEnrollment(enr); setRefundDialog(true); }}
                      onRecalculateBilling={RETIRED_TOOLS_VISIBLE ? handleRecalculateBilling : undefined}
                    />
                    {renderEnrollmentsPagination(activeEnrollments.length)}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="paused" className="mt-6">
            <Card className="border border-slate-200">
              <CardContent className="p-0">
                {pausedEnrollments.length === 0 ? (
                  <div className="p-12 text-center">
                    <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">{hasActiveFilters ? 'No paused enrollments match your filters' : 'No paused enrollments'}</p>
                  </div>
                ) : (
                  <>
                    <EnrollmentTable 
                      data={sliceForTab(pausedEnrollments)} 
                      showDates 
                      actionType="paused"
                      sortField={activeSortField}
                      sortDirection={activeSortDirection}
                      onSort={handleSort}
                      visibleColumns={visibleColumns}
                      selectedEnrollments={selectedEnrollments}
                      onToggleSelection={toggleEnrollmentSelection}
                      onSelectAll={toggleSelectAll}
                      onApprove={openApprovalDialog}
                      onReject={openRejectDialog}
                      onEdit={openEditDialog}
                      onMove={openMoveDialog}
                      onPause={openPauseDialog}
                      onCancel={openCancelDialog}
                      onResume={handleResume}
                      onCancelPause={handleCancelPause}
                      onEditPause={openEditPauseDialog}
                      onHistory={openHistoryDialog}
                      onRefund={(enr) => { setRefundEnrollment(enr); setRefundDialog(true); }}
                      onRecalculateBilling={RETIRED_TOOLS_VISIBLE ? handleRecalculateBilling : undefined}
                    />
                    {renderEnrollmentsPagination(pausedEnrollments.length)}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="canceled" className="mt-6">
            <Card className="border border-slate-200">
              <CardContent className="p-0">
                {canceledEnrollments.length === 0 ? (
                  <div className="p-12 text-center">
                    <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">{hasActiveFilters ? 'No canceled enrollments match your filters' : 'No canceled enrollments'}</p>
                  </div>
                ) : (
                  <>
                    <EnrollmentTable 
                      data={sliceForTab(canceledEnrollments)} 
                      showDates 
                      actionType="canceled"
                      sortField={activeSortField}
                      sortDirection={activeSortDirection}
                      onSort={handleSort}
                      visibleColumns={visibleColumns}
                      selectedEnrollments={selectedEnrollments}
                      onToggleSelection={toggleEnrollmentSelection}
                      onSelectAll={toggleSelectAll}
                      onApprove={openApprovalDialog}
                      onReject={openRejectDialog}
                      onEdit={openEditDialog}
                      onMove={openMoveDialog}
                      onPause={openPauseDialog}
                      onCancel={openCancelDialog}
                      onResume={handleResume}
                      onCancelPause={handleCancelPause}
                      onEditPause={openEditPauseDialog}
                      onHistory={openHistoryDialog}
                      onRefund={(enr) => { setRefundEnrollment(enr); setRefundDialog(true); }}
                      onRecalculateBilling={RETIRED_TOOLS_VISIBLE ? handleRecalculateBilling : undefined}
                    />
                    {renderEnrollmentsPagination(canceledEnrollments.length)}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* iter325 (Feb 2026) — Monthly Dashboard tab REMOVED.
              Superseded by the Forecast tab which covers the same
              use-case (per-enrollment monthly matrix + totals) with
              projection semantics the old dashboard lacked. Backend
              endpoint `/api/enrollments/admin/monthly-dashboard` has
              also been deleted; do NOT reintroduce this tab without
              also restoring the endpoint. */}

          <TabsContent value="forecast" data-testid="tabpanel-forecast">
            <EnrollmentForecastMatrix />
          </TabsContent>

          <TabsContent value="settings" data-testid="tabpanel-settings" className="mt-6">
            <div className="max-w-2xl">
              <BillingCadenceCard />
            </div>
          </TabsContent>
        </Tabs>

        {/* iter270: Approval dialog extracted. */}
        <ApprovalDialog
          open={approvalDialog}
          onOpenChange={setApprovalDialog}
          selectedEnrollment={selectedEnrollment}
          approvalData={approvalData}
          setApprovalData={setApprovalData}
          programs={programs}
          discounts={discounts}
          paymentFrequencies={PAYMENT_FREQUENCIES}
          selectedProgram={selectedProgram}
          siblingCheck={siblingCheck}
          checkingSibling={checkingSibling}
          getFilteredClasses={getFilteredClasses}
          onProgramChange={handleProgramChange}
          onClassChange={handleClassChange}
          onFrequencyChange={handleFrequencyChange}
          onDiscountChange={handleDiscountChange}
          recalculateFinalFee={recalculateFinalFee}
          onApprove={handleApprove}
          prorationPreview={prorationPreview}
          prorationPreviewLoading={prorationPreviewLoading}
        />

        {/* iter271: 5 more dialogs extracted (reject/pause/cancel/edit-pause/edit). */}
        <RejectEnrollmentDialog
          open={rejectDialog}
          onOpenChange={(o) => { setRejectDialog(o); if (!o) setSelectedEnrollment(null); }}
          selectedEnrollment={selectedEnrollment}
          rejectReason={rejectReason}
          setRejectReason={setRejectReason}
          onConfirm={handleReject}
        />

        <PauseEnrollmentDialog
          open={pauseDialog}
          onOpenChange={setPauseDialog}
          selectedEnrollment={selectedEnrollment}
          actionData={actionData}
          setActionData={setActionData}
          onConfirm={handlePause}
        />

        <CancelEnrollmentDialog
          open={cancelDialog}
          onOpenChange={setCancelDialog}
          selectedEnrollment={selectedEnrollment}
          actionData={actionData}
          setActionData={setActionData}
          onConfirm={handleCancel}
        />

        <EditPauseDialog
          open={editPauseDialog}
          onOpenChange={setEditPauseDialog}
          selectedEnrollment={selectedEnrollment}
          editPauseData={editPauseData}
          setEditPauseData={setEditPauseData}
          formatDateOnly={formatDateOnly}
          onConfirm={handleEditPause}
        />

        <EditEnrollmentDialog
          open={editDialog}
          onOpenChange={setEditDialog}
          selectedEnrollment={selectedEnrollment}
          editData={editData}
          setEditData={setEditData}
          paymentFrequencies={PAYMENT_FREQUENCIES}
          discounts={discounts}
          programs={programs}
          onConfirm={handleEdit}
        />

        {/* iter268: History Dialog extracted to its own component. */}
        <EnrollmentHistoryDialog
          open={historyDialog}
          onOpenChange={setHistoryDialog}
          selectedEnrollment={selectedEnrollment}
          historyLoading={historyLoading}
          historyData={historyData}
        />

        {/* iter269: Move Enrollment dialog extracted. */}
        <MoveEnrollmentDialog
          open={moveDialog}
          onOpenChange={setMoveDialog}
          selectedEnrollment={selectedEnrollment}
          programs={programs}
          moveData={moveData}
          setMoveData={setMoveData}
          moveLoading={moveLoading}
          onProgramChange={handleMoveProgramChange}
          getMoveFilteredClasses={getMoveFilteredClasses}
          onConfirm={handleMove}
        />

        {/* iter270: Quick Enroll dialog extracted (biggest one — 500L → component). */}
        <QuickEnrollDialog
          open={quickEnrollDialog}
          onOpenChange={setQuickEnrollDialog}
          quickEnrollData={quickEnrollData}
          setQuickEnrollData={setQuickEnrollData}
          filteredPlayers={filteredPlayers}
          parentStatusFilter={parentStatusFilter}
          setParentStatusFilter={setParentStatusFilter}
          playerStatusFilter={playerStatusFilter}
          setPlayerStatusFilter={setPlayerStatusFilter}
          playerSearch={playerSearch}
          setPlayerSearch={setPlayerSearch}
          playerListSortDirection={playerListSortDirection}
          setPlayerListSortDirection={setPlayerListSortDirection}
          programs={programs}
          discounts={discounts}
          paymentFrequencies={PAYMENT_FREQUENCIES}
          quickEnrollClasses={quickEnrollClasses}
          quickEnrollLoading={quickEnrollLoading}
          onProgramChange={handleQuickEnrollProgramChange}
          onClassToggle={handleQuickEnrollClassToggle}
          onFrequencyChange={handleQuickEnrollFrequencyChange}
          onSubmit={handleQuickEnroll}
        />

        {/* iter321g — Multi-parent picker (opens when the backend
            refuses with 409 multi_parent_confirm_required). */}
        <MultiParentEnrollPromptDialog
          open={!!multiParentPrompt}
          prompt={multiParentPrompt}
          loading={quickEnrollLoading}
          onCancel={() => setMultiParentPrompt(null)}
          onConfirm={(pid) => handleQuickEnroll(false, pid)}
        />

        {/* iter269: Import Enrollments dialog extracted. */}
        <ImportEnrollmentsDialog
          open={importDialog}
          onOpenChange={resetImportDialog}
          fileInputRef={fileInputRef}
          setImportFile={setImportFile}
          importFile={importFile}
          createSubscriptions={createSubscriptions}
          setCreateSubscriptions={setCreateSubscriptions}
          importLoading={importLoading}
          importResults={importResults}
          onImport={handleImport}
          onDownloadTemplate={handleDownloadTemplate}
        />

        {/* iter269: Bulk Action dialog extracted. */}
        <BulkActionDialog
          open={bulkActionDialog}
          onOpenChange={setBulkActionDialog}
          actionType={bulkActionType}
          selectedCount={selectedEnrollments.length}
          data={bulkActionData}
          setData={setBulkActionData}
          loading={bulkActionLoading}
          onExecute={executeBulkAction}
        />

      </div>

      <RefundDialog
        open={refundDialog}
        onOpenChange={setRefundDialog}
        entityType="enrollment"
        entity={refundEnrollment}
        priceField="total_amount"
        summaryLines={
          refundEnrollment
            ? [
                { label: 'Student', value: refundEnrollment.player_name || refundEnrollment.parent_name || '—' },
                { label: 'Class', value: refundEnrollment.class_name || refundEnrollment.program_name || '—' },
              ]
            : []
        }
        allowCancelCheckbox={false}
        onRefunded={() => { setRefundDialog(false); setRefundEnrollment(null); fetchData(); }}
      />

      {/* iter307be — Billing Cycles Import dialog */}
      <Dialog open={billingCyclesImportDialog} onOpenChange={(o) => !o && resetBillingCyclesDialog()}>
        <DialogContent className="max-w-2xl" data-testid="billing-cycles-import-dialog">
          <DialogHeader>
            <DialogTitle>Import Billing Cycles (Update / Delete)</DialogTitle>
            <DialogDescription>
              Upload an edited copy of the exported CSV. Each row's <code>action</code> column drives the change:
              <code> delete</code> · <code>update</code> · <code>update_enrollment</code> · <code>update_both</code>.
              Run a <strong>dry-run first</strong> to preview, then re-upload with dry-run unchecked to commit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm">CSV file</Label>
              <Input
                type="file" accept=".csv,text/csv"
                onChange={(e) => { setBillingCyclesFile(e.target.files?.[0] || null); setBillingCyclesResult(null); }}
                data-testid="billing-cycles-import-file"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={billingCyclesDryRun}
                onChange={(e) => setBillingCyclesDryRun(e.target.checked)}
                data-testid="billing-cycles-import-dryrun"
              />
              <span>Dry-run (preview without writing)</span>
            </label>
            {billingCyclesResult && (
              <div className="bg-slate-50 border rounded-md p-3 text-xs space-y-1" data-testid="billing-cycles-import-result">
                <div><strong>Mode:</strong> {billingCyclesResult.dry_run ? 'Dry-run preview' : 'Committed'}</div>
                <div><strong>Summary:</strong></div>
                <ul className="ml-4 list-disc">
                  <li>Deleted: {billingCyclesResult.summary?.deleted ?? 0}</li>
                  <li>Updated invoices: {billingCyclesResult.summary?.updated_invoices ?? 0}</li>
                  <li>Updated enrollments: {billingCyclesResult.summary?.updated_enrollments ?? 0}</li>
                  <li>Skipped: {billingCyclesResult.summary?.skipped ?? 0}</li>
                  <li>Errors: {billingCyclesResult.summary?.errors ?? 0}</li>
                </ul>
                {billingCyclesResult.errors && billingCyclesResult.errors.length > 0 && (
                  <div className="mt-2">
                    <strong>Errors:</strong>
                    <ul className="ml-4 list-disc">
                      {billingCyclesResult.errors.slice(0, 20).map((er, i) => (
                        <li key={i} className="text-rose-700">Line {er.line}: {er.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetBillingCyclesDialog} data-testid="billing-cycles-import-cancel">
              Close
            </Button>
            <Button
              onClick={handleImportBillingCycles}
              disabled={billingCyclesLoading || !billingCyclesFile}
              className={billingCyclesDryRun ? '' : 'bg-rose-600 hover:bg-rose-700'}
              data-testid="billing-cycles-import-submit"
            >
              {billingCyclesLoading
                ? 'Processing…'
                : billingCyclesDryRun
                  ? 'Run Dry-Run Preview'
                  : 'Commit Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Enrollments;
